import {
  Button
} from 'reactstrap'
import { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import TlGoogleMapsApi from 'api/tl-google-maps-api'
import { GoogleMap, Autocomplete } from '@react-google-maps/api'
import style from './user-address-pinpoint.module.scss'
import pinpointIcon from 'assets/img/current-location.png'

const propTypes = {
  provinceName  : PropTypes.string,
  cityName      : PropTypes.string,
  districtName  : PropTypes.string,
  lat           : PropTypes.number,
  long          : PropTypes.number,
  setFieldValue : PropTypes.func,
  closePinpoint : PropTypes.func
}

/**
 * Component for user to pick lat and long of their address from GoogleMaps
 * @param {string} provinceName - to find initial center location based on province name, will be used if lat and long value empty
 * @param {string} cityName - to find initial center location based on city name, will be used if lat and long value empty
 * @param {string} districtName - to find initial center location based on district name, will be used if lat and long value empty
 * @param {number} lat - initial lat value for center pointer
 * @param {number} long - initial long value for center pointer
 * @param {function} setFieldValue - formik function
 * @param {function} closePinpoint - close pinpoint function
 */
const UserAddressPinpoint = ({
  provinceName,
  cityName,
  districtName,
  lat,
  long,
  setFieldValue,
  closePinpoint
}) => {
  const mapRef = useRef(null)

  const [ isFetchingLocation, setIsFetchingLocation ]       = useState(true)
  const [ isGettingUserLocation, setIsGettingUserLocation ] = useState(false)

  const [ searchAddressResult, setSearchAddressResult ] = useState(null)

  // set default location near tinkerlust in case fetching map error from api
  const [ userLocation, setUserLocation ] = useState({
    lat : parseFloat(process.env.REACT_APP_DEFAULT_LATITUDE),
    lng : parseFloat(process.env.REACT_APP_DEFAULT_LONGITUDE)
  })

  const showGoogleMap = !isFetchingLocation

  useEffect(() => {

    setIsFetchingLocation(true)
    if (lat && long) {
      // need to setUserLocation here because somehow google maps not re-center the map
      // if we put lat and long directly to userLocation state initial value
      setUserLocation((prevLocation) => ({
        ...prevLocation,
        lat : lat,
        lng : long
      }))
      setIsFetchingLocation(false)
    } else {
      // no need to fetch google api if lat and long exist
      // fetch initial center value to google maps api based on province, city value
      // by doing this, it will be easier for user to find their address
      const userAddress = [
        encodeURIComponent(districtName),
        encodeURIComponent(cityName),
        encodeURIComponent(provinceName),
        'Indonesia'
      ].join(', ')
      TlGoogleMapsApi.getUserAreaLocation({ userAddress })
        .then((response) => {
          const geocodeData  = response
          const { lat, lng } = geocodeData.results[0].geometry.location
          setUserLocation((prevLocation) => ({
            ...prevLocation,
            lat : lat,
            lng : lng
          }))
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => setIsFetchingLocation(false))
    }
  }, [ lat, long, provinceName, cityName, districtName ])

  const handleDragEnd = () => {
    const newLat = mapRef.current.center.lat()
    const newLng = mapRef.current.center.lng()
    setUserLocation((prevLocation) => ({
      ...prevLocation,
      lat : newLat,
      lng : newLng
    }))
  }

  const handleSubmitPinpoint = () => {
    const newLat = mapRef.current.center.lat()
    const newLng = mapRef.current.center.lng()
    setFieldValue('latitude', newLat)
    setFieldValue('longitude', newLng)
    setUserLocation((prevLocation) => ({
      ...prevLocation,
      lat : newLat,
      lng : newLng
    }))
    closePinpoint()
  }

  const handleUseMyLocationClick = () => {
    // get lat and long using current address location
    // user need to allow browser access their location

    if (navigator.geolocation) {
      // disable button `Gunakan lokasi saya` when navigating to current user location
      setIsGettingUserLocation(true)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude  = position.coords.latitude
          const longitude = position.coords.longitude
          setUserLocation((prevLocation) => ({
            ...prevLocation,
            lat : latitude,
            lng : longitude
          }))
        },
        (error) => {
          // show alert if error happen when getting user location
          // e.g error when user is not allowing our app to access their location
          alert('Terjadi error saat mendapatkan lokasi.')
          console.error('Error getting geolocation:', error)
        }
      )

    } else {
      // show alert if browser is not supported
      const alertMsg = [
        'Fitur geolocation tidak didukung oleh browser ini.',
        'Mohon coba gunakan browser lain dan coba lagi.'
      ].join('\n\n')
      alert(alertMsg)
    }
  }

  const onSearchAddressLoad = (autocomplete) => {
    setSearchAddressResult(autocomplete)
  }

  const onSearchAddressChange = () => {
    if (searchAddressResult != null) {
      const place         = searchAddressResult.getPlace()
      const geometry      = place.geometry
      const geometryExist = !!geometry
      if (!(geometryExist)) return

      const newLat = geometry.location.lat()
      const newLng = geometry.location.lng()
      setUserLocation({
        lat : newLat,
        lng : newLng
      })
    }
  }

  // to disable trigger google maps function on enter press
  const onEnterKeyPress = (event) => {
    // enter key code
    if (event.keyCode === 13) {
      event.preventDefault()
    }
  }

  return (
    <>
      {
        showGoogleMap && (
          <GoogleMap
            zoom={ 20 }
            center={ userLocation }
            mapContainerClassName={ `w-100 ${style.mapContainer}` }
            options={{
              fullscreenControl : false,
              streetViewControl : false,
              mapTypeControl    : false,
              // to allow dragging with 1 finger in mobile view
              gestureHandling   : 'greedy'
            }}
            onLoad={ (map) => mapRef.current = map }
            onDragEnd={ handleDragEnd }
            // re-enable button `Gunakan Lokasi saya` after navigated to current location and map fully loaded
            onTilesLoaded={ () => setIsGettingUserLocation(false) }
          >
            <div className={ `px-3 ${style.searchFormLocation}` }>
              <Autocomplete
                onPlaceChanged={ onSearchAddressChange }
                onLoad={ onSearchAddressLoad }
                className='d-flex align-items-center shadow'
              >
                <input
                  id='address-pinpoint-search-field'
                  className={ 'form-control border-0' }
                  type="text"
                  placeholder='Cari alamatmu disini'
                  onKeyDown={ onEnterKeyPress }
                  onKeyUp={ onEnterKeyPress }
                />
              </Autocomplete>
            </div>

            <div className={ `d-flex flex-column align-items-center ${style.pinpointIcon}` }>
              <strong className='mb-1 bg-danger p-1 text-white rounded'>
                { 'Alamatmu disini' }
              </strong>
              <img
                width={ 27 }
                height={ 40 }
                src={ pinpointIcon }
                alt={ 'current location' }
              />
            </div>

            <Button
              color='white'
              onClick={ handleUseMyLocationClick }
              className={ `bg-white rounded font-weight-bold shadow ${style.myLocationButton}` }
              disabled={ isGettingUserLocation }
            >
              <i className='fa fa-map-marker mr-2 text-primary' />
              { 'Gunakan Lokasi Saya' }
            </Button>
          </GoogleMap>
        )
      }

      <div className='p-2'>
        <Button
          color='primary'
          block
          onClick={ handleSubmitPinpoint }
        >
          { 'Pilih Lokasi' }
        </Button>

        <Button
          outline
          block
          color='primary'
          onClick={ closePinpoint }
        >
          { 'Kembali' }
        </Button>
      </div>
    </>
  )
}

UserAddressPinpoint.propTypes = propTypes

export default UserAddressPinpoint
